import type { ComputedRef } from "vue";
import { computed, watch } from "vue";
import type { Bolt_On_Discharges, Pumps } from "gql/api";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type { FormItems } from "@/composables/forms/formTypes";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import {
  findBestMatchingDischarge,
  hasAutomaticDischargeInputChanges
} from "@/models/equipment/bolt_on_discharges/properties";
import { useBoltOnDischarges } from "./boltOnDischarges";

type AutomaticBoltOnDischargeOptions = {
  equipmentForm: FormItems["equipment"];
  pumpsForm: FormItems["pumps"];
  scenarioForm: FormItems["scenario"];
  wellPropertiesForm: FormItems["wellProperties"];
  yToolEquipmentTubingsForm: FormItems["yToolEquipmentTubings"];
  bottomTubingOuterDiameterInches: ComputedRef<number | null | undefined>;
};

/**
 * Keeps the automatic ESP bolt-on discharge selection in step with the pump and
 * the tubing.
 *
 * This runs where the scenario is provided rather than in the row component,
 * because the row mounts on the Parameters page alone. The schematic, the
 * design overview, the charts and the export all read the same form. A
 * derivation that runs on one page only gives those screens a different answer
 * for the same scenario, and the answer changes the depth of the pumps.
 *
 * The selection is applied without a diff while it stays derived, so an
 * untouched scenario reports no unsaved change. Once the user changes an input
 * that can affect the match, the selection becomes a normal form update and is
 * saved with the scenario.
 *
 * A unique match needs the pump housing series and the bottom-most tubing
 * diameter. Missing prerequisites or an ambiguous catalog result clear the
 * selection. HPS scenarios choose a discharge by hand, so this does nothing for
 * them.
 *
 * A read-only scenario is never changed, and neither is a legacy import. A
 * legacy sizing must keep the equipment it was imported with. An admin has
 * elevated privileges, so isReadonly is false for them and the legacy flag must
 * be checked separately.
 */
export function useAutomaticBoltOnDischarge({
  equipmentForm,
  pumpsForm,
  scenarioForm,
  wellPropertiesForm,
  yToolEquipmentTubingsForm,
  bottomTubingOuterDiameterInches
}: AutomaticBoltOnDischargeOptions): void {
  const { customerEligibleRows: boltOnDischargeCatalog } =
    useBoltOnDischarges(false);
  const { isReadonly, isImportedFromLegacy } = useEditPermissions(
    undefined,
    scenarioForm
  );

  const equipment = computed(() => equipmentForm.data.value);

  const isHps = computed(
    () => scenarioForm.data.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps
  );

  // The discharge mounts on the uppermost pump, which is the first of the order.
  const topPump = computed<Partial<Pumps> | null>(() => {
    const order = equipment.value?.pumps_order;
    if (!Array.isArray(order)) return null;
    for (const xrefId of order) {
      if (typeof xrefId !== "string") continue;
      const xref = pumpsForm.data.value[xrefId];
      if (xref) return xref.r_pump || null;
    }
    return null;
  });

  const selectedBoltOnDischarge = computed(
    () => equipment.value?.r_bolt_on_discharge
  );

  const selectedBoltOnDischargeId = computed(
    () => equipment.value?.bolt_on_discharge_id
  );

  function writeSelection(boltOnDischarge: Bolt_On_Discharges | null) {
    equipmentForm.update({
      r_bolt_on_discharge: boltOnDischarge,
      bolt_on_discharge_id: boltOnDischarge?.id ?? null
    } as Partial<typeof equipmentForm.data.value>);
  }

  function writeSelectionWithoutDiff(
    boltOnDischarge: Bolt_On_Discharges | null
  ) {
    equipmentForm.updateWithoutDiff({
      r_bolt_on_discharge: boltOnDischarge,
      bolt_on_discharge_id: boltOnDischarge?.id ?? null
    } as Partial<typeof equipmentForm.data.value>);
  }

  const automaticSelectionInputsChanged = computed(() =>
    hasAutomaticDischargeInputChanges({
      equipment: equipment.value,
      equipmentDiff: equipmentForm.diff.value,
      pumpsDiff: pumpsForm.diff.value,
      wellPropertiesDiff: wellPropertiesForm.diff.value,
      yToolEquipmentTubingsDiff: yToolEquipmentTubingsForm.diff.value
    })
  );

  watch(
    [
      () => topPump.value?.id,
      () => topPump.value?.housing_outer_diameter_inches,
      bottomTubingOuterDiameterInches,
      selectedBoltOnDischargeId,
      boltOnDischargeCatalog,
      automaticSelectionInputsChanged,
      isHps
    ],
    () => {
      if (isHps.value) return;
      if (isReadonly.value || isImportedFromLegacy.value) return;
      const hasPrerequisites =
        !!topPump.value?.id &&
        Number(topPump.value.housing_outer_diameter_inches) > 0 &&
        Number(bottomTubingOuterDiameterInches.value) > 0;
      if (!hasPrerequisites) {
        if (selectedBoltOnDischargeId.value || selectedBoltOnDischarge.value) {
          if (automaticSelectionInputsChanged.value) {
            writeSelection(null);
          } else {
            writeSelectionWithoutDiff(null);
          }
        }
        return;
      }
      if (!boltOnDischargeCatalog.value?.length) return;

      const best = findBestMatchingDischarge(
        topPump.value,
        bottomTubingOuterDiameterInches.value,
        boltOnDischargeCatalog.value
      );
      if (automaticSelectionInputsChanged.value) {
        writeSelection(best);
        return;
      }
      if (
        (best?.id ?? null) !== (selectedBoltOnDischargeId.value ?? null) ||
        (best?.id ?? null) !== (selectedBoltOnDischarge.value?.id ?? null)
      ) {
        writeSelectionWithoutDiff(best);
      }
    },
    { immediate: true }
  );
}
